import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Alert from 'antd/lib/alert';
import Modal from 'antd/lib/modal';
import type { UploadFile } from 'antd/lib/upload/interface';

import { createReferenceAsync } from 'actions/references-actions';
import { DropZone, originals } from 'components/project-page/new-task-modal';
import { isPhoto, referencePayload } from 'components/project-page/new-task-form';
import { t } from 'utils/i18n';

interface Props {
    projectId: number;
    taskId: number;
    taskName: string;
    onClose(): void;
    onCreated(): void;
}

// the photos become a reference named after the task and linked to it
export default function AddPhotosModal({
    projectId, taskId, taskName, onClose, onCreated,
}: Props): JSX.Element {
    const dispatch = useDispatch();
    const [photos, setPhotos] = useState<UploadFile[]>([]);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const busy = status !== null;

    const create = async (): Promise<void> => {
        const payload = referencePayload({
            projectId, name: taskName, drawings: [], photos: originals(photos),
        }, taskId);
        if (!payload) {
            setError(t('Add at least one photo'));
            return;
        }
        setError(null);
        setStatus(t('Adding the photos…'));
        try {
            await (dispatch(createReferenceAsync(payload, setStatus)) as unknown as Promise<void>);
            onCreated();
        } catch (e: unknown) {
            setStatus(null);
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    return (
        <Modal
            visible
            title={t('Add photos')}
            okText={t('Add')}
            onOk={create}
            onCancel={onClose}
            okButtonProps={{ disabled: busy || !photos.length, loading: busy }}
            cancelButtonProps={{ disabled: busy }}
            closable={!busy}
            maskClosable={false}
        >
            <DropZone
                label={t('Photos of the rooms')}
                hint={t('Furniture on them is matched to the library and placed on the drawings')}
                files={photos}
                accepts={isPhoto}
                disabled={busy}
                onChange={setPhotos}
            />
            {status && <Alert type='info' showIcon message={status} className='cvat-task-page-modal-note' />}
            {error && <Alert type='error' showIcon message={error} className='cvat-task-page-modal-note' />}
        </Modal>
    );
}
